import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Trophy, Loader2, ArrowUp, ArrowDown } from 'lucide-react';
import { LeagueRankingData } from '@/hooks/useLeagueRanking';

interface LeagueRankingByEncounterProps {
  rankings: LeagueRankingData[];
  selectedEncounter: number;
  onEncounterChange: (encounter: number) => void;
  availableEncounters: number[];
  isLoading: boolean;
  gameType: 'singles' | 'doubles';
  onShowBrackets?: () => void;
}

const LeagueRankingByEncounter: React.FC<LeagueRankingByEncounterProps> = ({
  rankings,
  selectedEncounter,
  onEncounterChange,
  availableEncounters,
  isLoading,
  gameType,
  onShowBrackets
}) => {
  if (isLoading) {
    return (
      <Card className="bg-card">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  // Helper to render movement badge
  const renderMovementBadge = (player: LeagueRankingData) => {
    const isUp = player.movementType === 'up';
    const isDown = player.movementType === 'down';

    if (!player.movementLabel || player.movementLabel === '=' || player.movementLabel === '-') {
      return (
        <Badge variant="outline" className="text-xs">
          Mantiene
        </Badge>
      );
    }

    return (
      <Badge 
        className={`text-xs ${
          isUp ? 'bg-green-600 text-white' : 
          isDown ? 'bg-red-600 text-white' : 
          ''
        }`}
        variant={!isUp && !isDown ? 'outline' : 'default'}
      >
        {isUp && <ArrowUp className="h-3 w-3 mr-1" />}
        {isDown && <ArrowDown className="h-3 w-3 mr-1" />}
        {player.movementLabel}
      </Badge>
    );
  };


  return (
    <Card className="bg-card">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Trophy className="h-6 w-6 text-yellow-500" />
            <CardTitle className="text-xl font-bold">Ranking de la Liga</CardTitle>
          </div>
          <Select 
            value={String(selectedEncounter)} 
            onValueChange={(val) => {
              onEncounterChange(parseInt(val));
            }}
          >
            <SelectTrigger className="w-[250px]">
              <SelectValue placeholder="Ver ranking" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="0">Ranking Inicial</SelectItem>
              {availableEncounters.map((num) => (
                <SelectItem key={num} value={String(num)}>
                  Encuentro {num}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {/* Table Headers */}
        <div className="grid grid-cols-12 gap-2 p-3 bg-muted rounded-lg mb-3 font-semibold text-xs text-muted-foreground">
          <div className="col-span-1 text-center">#</div>
          <div className="col-span-7">Pareja</div>
          <div className="col-span-4 text-center">Movimiento</div>
        </div>

        {/* Rankings */}
        <div className="space-y-2">
          {rankings.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground">
              No hay datos de ranking disponibles
            </div>
          ) : (
            rankings.map((player, index) => (
              <div
                key={player.registrationId}
                className={`grid grid-cols-12 gap-2 items-center p-3 rounded-lg border transition-all ${
                  player.isCurrentUser 
                    ? 'bg-primary/10 border-primary' 
                    : player.position <= 4
                      ? 'bg-green-50 border-green-200'
                      : 'bg-card border-border'
                }`}
              >
                {/* Position */}
                <div className="col-span-1 flex justify-center">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-sm ${
                    player.position === 1
                      ? 'bg-yellow-500 text-white'
                      : player.position === 2
                        ? 'bg-gray-400 text-white'
                        : player.position === 3
                          ? 'bg-amber-600 text-white'
                          : player.position <= 4
                            ? 'bg-green-500 text-white'
                            : 'bg-muted text-muted-foreground'
                  }`}>
                    {player.position}
                  </div>
                </div>

                {/* Pair Names - Both players */}
                <div className="col-span-7">
                  <div className={`font-medium text-sm ${player.isCurrentUser ? 'text-primary' : 'text-foreground'}`}>
                    {player.player1Name}
                    {gameType === 'doubles' && player.player2Name && (
                      <span className="text-muted-foreground"> / {player.player2Name}</span>
                    )}
                  </div>
                  {player.isCurrentUser && (
                    <div className="text-xs text-primary font-medium">TÚ</div>
                  )}
                </div>

                {/* Movement Badge */}
                <div className="col-span-4 flex justify-center">
                  {renderMovementBadge(player)}
                </div>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default LeagueRankingByEncounter;
